// packages/web/src/components/marketplace/SearchFilters.tsx
// Search and filter controls component with URL-synced state

import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Box, Chip, InputAdornment, Stack, TextField } from '@mui/material';
import React, { useState, useEffect } from 'react';

interface SearchFiltersProps {
  category: string;
  onCategoryChange: (category: string) => void;
  categories: string[];
  loading?: boolean;
  query?: string;
  onQueryChange?: (query: string) => void;
  hideChips?: boolean;
}

/**
 * Component for search input and category filters
 * Filters sync with URL params for SEO crawlability
 */
export function SearchFilters({
  category,
  onCategoryChange,
  categories,
  loading = false,
  query = '',
  onQueryChange,
  hideChips = false,
}: SearchFiltersProps) {
  // Local state for debounced search input
  const [localQuery, setLocalQuery] = useState(query);

  // Sync local state when URL param changes
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  // Debounce search input — update URL after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onQueryChange && localQuery !== query) {
        onQueryChange(localQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localQuery, query, onQueryChange]);

  return (
    <>
      {/* Search Input */}
      {onQueryChange && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search services..."
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
      )}

      {/* Category Filters */}
      {!hideChips && (<Box sx={{ width: '100%', mb: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-evenly"
          flexWrap="wrap"
          useFlexGap
          sx={{
            width: '100%',
            '& > *': { flex: '1 1 auto', minWidth: 'fit-content' },
          }}
        >
          {categories.map((cat) => {
            const isAiSkills = cat === 'AI Skills';
            return (
              <Chip
                key={cat}
                icon={isAiSkills ? <SmartToyIcon sx={{ fontSize: 18 }} /> : undefined}
                label={cat}
                clickable
                color={category === cat ? 'primary' : 'default'}
                variant={category === cat ? 'filled' : 'outlined'}
                onClick={() => onCategoryChange(category === cat ? '' : cat)}
                sx={{
                  flex: 1,
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  ...(isAiSkills && category !== cat
                    ? {
                        borderColor: '#7c4dff',
                        color: '#7c4dff',
                        fontWeight: 700,
                        '&:hover': { bgcolor: 'rgba(124,77,255,0.08)' },
                      }
                    : {}),
                  ...(isAiSkills && category === cat
                    ? {
                        bgcolor: '#7c4dff',
                        color: '#fff',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#651fff' },
                      }
                    : {}),
                }}
              />
            );
          })}
        </Stack>
      </Box>)}
    </>
  );
}
